import { useRef, useEffect, useState, type ElementType } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Flex,
  Icon,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Button,
  Spinner,
} from '@chakra-ui/react';
import { VolumeMax } from '@kula-ai/icons/outline/VolumeMax';
import { VolumeX } from '@kula-ai/icons/outline/VolumeX';
import { FaPlay, FaPause } from 'react-icons/fa';
import { KText } from '@/ui-library/components/text';
import { selectTimeToJump } from '@/store/interview-video/selectors';
import { updateCurrentTime } from '@/store/interview-video';
import {
  setIsPlaying,
  setIsMute,
  setPlaybackSpeed,
  setProgress,
  setDuration,
} from '@/store/interview-video';
import {
  selectIsPlaying,
  selectIsMute,
  selectPlaybackSpeed,
  selectProgress,
  selectDuration,
  selectCurrentTime,
} from '@/store/interview-video/selectors';
import { InterviewTranscriptPanel } from '@/components/shared/interviews-revamp/interview-transcript/InterviewTranscriptPanel';
import { InterviewSummary } from '@/components/shared/interviews-revamp/component/InterviewSummary';
import { type InterviewType } from '../types/interview';
import { Minimize02 } from '@kula-ai/icons/outline/Minimize02';
import { useHotkeys } from 'react-hotkeys-hook';

interface ModalVideoPlayerProps {
  videoUrl: string;
  transcriptData: any;
  currentTime: number;
  handleTimeJump: (_time: number, _shouldPlay?: boolean) => void;
  interview: InterviewType;
  isLoading: boolean;
  onClose: () => void;
}

export const ModalVideoPlayer = ({
  videoUrl,
  transcriptData,
  currentTime,
  handleTimeJump,
  interview,
  isLoading: isTranscriptLoading,
  onClose = () => {},
}: ModalVideoPlayerProps) => {
  const dispatch = useDispatch();
  const isPlaying = useSelector(selectIsPlaying);
  const isMute = useSelector(selectIsMute);
  const playbackSpeed = useSelector(selectPlaybackSpeed);
  const progress = useSelector(selectProgress);
  const duration = useSelector(selectDuration);
  const timeJumpValue = useSelector(selectTimeToJump);
  const reduxCurrentTime = useSelector(selectCurrentTime);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentTab, setCurrentTab] = useState<string>('recording');
  const transcriptPanelRef = useRef<any>(null);
  const controlsRef = useRef<HTMLDivElement>(null);

  const tabOptions = [
    {
      label: 'Summary',
      value: 'summary',
    },
    {
      label: 'Recording',
      value: 'recording',
    },
  ];

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (timeJumpValue && videoRef.current) {
      videoRef.current.currentTime = timeJumpValue / 1000;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync loading with seek
      setIsLoading(true);
    }
  }, [timeJumpValue]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = reduxCurrentTime || 0;
      if (isPlaying) {
        void videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.onloadedmetadata = () => {
        dispatch(setDuration(videoRef?.current?.duration || 0));
      };
      videoRef.current.ontimeupdate = () => {
        dispatch(setProgress(videoRef?.current?.currentTime || 0));
      };
      videoRef.current.onended = () => {
        dispatch(setIsPlaying(false));
      };
    }
  }, [videoRef]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const video = videoRef?.current;

    const handleCanPlayThrough = (): void => {
      setIsLoading(false);
    };

    video?.addEventListener('canplaythrough', handleCanPlayThrough);

    return () => {
      video?.removeEventListener('canplaythrough', handleCanPlayThrough);
    };
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMute;
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [isMute, playbackSpeed]);

  // Sync transcript when user interacts with video controls
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;

    const handleControlsInteraction = (e: Event) => {
      const target = e.target as HTMLElement;
      if (controls.contains(target) || target === controls) {
        transcriptPanelRef.current?.resetManualScroll?.();
      }
    };

    // Use capture phase to catch all events
    controls.addEventListener('click', handleControlsInteraction, true);
    controls.addEventListener('touchstart', handleControlsInteraction, true);
    controls.addEventListener('pointerdown', handleControlsInteraction, true);

    return () => {
      controls.removeEventListener('click', handleControlsInteraction, true);
      controls.removeEventListener(
        'touchstart',
        handleControlsInteraction,
        true
      );
      controls.removeEventListener(
        'pointerdown',
        handleControlsInteraction,
        true
      );
    };
  }, []);

  const handleTime = (): void => {
    const currentTime = videoRef.current?.currentTime;
    if (currentTime !== undefined) {
      dispatch(updateCurrentTime(currentTime));
    }
  };

  const togglePlayPause = (): void => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      dispatch(setIsPlaying(!isPlaying));
    }
  };

  const skipBack = (): void => {
    if (videoRef.current) {
      videoRef.current.currentTime -= 5;
    }
  };

  const skipForward = (): void => {
    if (videoRef.current) {
      videoRef.current.currentTime += 5;
    }
  };

  const handleVolumeChange = (): void => {
    if (videoRef.current) {
      dispatch(setIsMute(!isMute));
      videoRef.current.muted = !isMute;
    }
  };

  const handleProgressChange = (value: number): void => {
    if (videoRef.current) {
      videoRef.current.currentTime = value;
      dispatch(setProgress(value));
    }
  };

  const handlePlaybackSpeed = (): void => {
    if (videoRef.current) {
      const newSpeed = playbackSpeed >= 2 ? 1 : playbackSpeed + 0.25;
      dispatch(setPlaybackSpeed(newSpeed));
      videoRef.current.playbackRate = newSpeed;
    }
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  useHotkeys('left', skipBack, { enabled: !isLoading });
  useHotkeys('right', skipForward, { enabled: !isLoading });

  return (
    <Flex
      height="100%"
      maxHeight="100%"
      width="100%"
      bg="#000"
      borderRadius="lg"
      overflow="hidden"
    >
      {/* Video Section */}
      <Flex
        flex="2"
        alignItems="center"
        justifyContent="center"
        bg="#000"
        height="100%"
        minHeight="0"
      >
        <Box
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          {isLoading && (
            <Spinner size="sm" color="primary-500" pos="absolute" />
          )}
          <video
            ref={videoRef}
            src={videoUrl}
            width="100%"
            height="100%"
            style={{
              maxHeight: '70vh',
              objectFit: 'contain',
              background: '#000',
            }}
            onTimeUpdate={handleTime}
            disablePictureInPicture
            controlsList="nodownload noplaybackrate"
            onClick={(e) => {
              togglePlayPause();
              e.stopPropagation();
              e.preventDefault();
            }}
          />
          {/* Controls at the bottom */}
          <Flex
            ref={controlsRef}
            width="100%"
            px="32px"
            py="16px"
            alignItems="center"
            gap="20px"
            bg="rgba(0,0,0,0.7)"
            borderBottomLeftRadius="16px"
            borderBottomRightRadius="16px"
          >
            <Button
              variant="primary"
              onClick={togglePlayPause}
              bg="base-white"
              borderRadius="50%"
              p="0"
              minW="2rem"
              w="2rem"
              h="2rem"
              _hover={{}}
              _focus={{}}
              _active={{}}
            >
              {!isPlaying ? (
                <Icon
                  as={FaPlay as ElementType}
                  w="12px"
                  h="12px"
                  color="base-black"
                />
              ) : (
                <Icon
                  as={FaPause as ElementType}
                  w="12px"
                  h="12px"
                  color="base-black"
                />
              )}
            </Button>
            <Flex alignItems="center" gap="8px" minW="80px" flex="1">
              <KText color="#fff" minW="40px">
                {formatTime(progress)}
              </KText>
              <Slider
                aria-label="slider-ex-1"
                value={progress}
                max={duration}
                onChange={handleProgressChange}
                focusThumbOnChange={false}
                flex="1"
                minW="120px"
              >
                <SliderTrack bg="rgba(255, 255, 255, 0.5)" h="10px">
                  <SliderFilledTrack bg="primary-500" />
                </SliderTrack>
                <SliderThumb w="20px" h="20px" />
              </Slider>
              <KText color="base-white" minW="40px" textAlign="right">
                {formatTime(duration)}
              </KText>
            </Flex>
            <KText
              color="base-white"
              cursor="pointer"
              onClick={handlePlaybackSpeed}
            >
              {playbackSpeed}x
            </KText>
            <Flex onClick={handleVolumeChange} cursor="pointer">
              {!isMute ? (
                <Icon as={VolumeMax} w="20px" h="20px" color="base-white" />
              ) : (
                <Icon as={VolumeX} w="20px" h="20px" color="base-white" />
              )}
            </Flex>
            <Icon
              as={Minimize02}
              w="20px"
              h="20px"
              color="base-white"
              cursor="pointer"
              onClick={onClose}
            />
          </Flex>
        </Box>
      </Flex>
      {/* Transcript/Recording Panel */}
      <Flex
        flex="1.2"
        flexDir="column"
        bg="#fff"
        height="100%"
        minHeight="0"
        borderTopRightRadius="16px"
        borderBottomRightRadius="16px"
        boxShadow="0 0 24px rgba(0,0,0,0.08)"
        overflowY="auto"
      >
        <Tabs
          display="flex"
          flexDir="column"
          flex="1"
          h="100%"
          alignItems="stretch"
          index={tabOptions.findIndex((option) => option?.value === currentTab)}
          isLazy={true}
        >
          <TabList>
            {tabOptions.map((option) => {
              return (
                <Tab
                  key={option?.value}
                  _active={{ boxShadow: '' }}
                  _focus={{ boxShadow: '' }}
                  _selected={{
                    color: 'ats-primary',
                    borderColor: 'ats-primary',
                  }}
                  onClick={() => {
                    setCurrentTab(option?.value);
                  }}
                  alignItems="center"
                  mx="lg"
                  pt="4px"
                  pb="12px"
                  px="0px"
                  minH="38px"
                  mt="18px"
                >
                  <KText color="inherit" size="sm-medium">
                    {option?.label}
                  </KText>
                </Tab>
              );
            })}
          </TabList>
          <TabPanels h="100%" overflowY="hidden">
            <TabPanel p="0" h="100%" overflowY="hidden" flex="1">
              <InterviewSummary interview={interview} />
            </TabPanel>
            <TabPanel p="0" h="100%" overflowY="hidden" flex="1">
              <InterviewTranscriptPanel
                ref={transcriptPanelRef}
                data={transcriptData}
                currentTime={currentTime}
                handleTimeJump={handleTimeJump}
                playerType="modal"
                isLoading={isTranscriptLoading}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
    </Flex>
  );
};
